/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FeatureName,
  LIST_ASSETS,
  PermissionValues,
  useAssetListFilter,
  useAssetListOrder,
  useCurrentAccount,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useSetAssetListOrder,
  WorkspaceType,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid, SelectionStatus } from '../infiniteDataGrid';
import { AssetsListOld } from './assetsList-old';
import { useColumns } from './useColumns';

export type AssetListProps = {
  onAssetClick: any;
  maxHeight?: number;
  paddingBottom?: number;
  onAssetsCountChange?: (count: number) => void;
  updateRowsSelected?: (rows: Array<any>) => void;
  preserveScrollKey?: string;
  addToGroupsComponent?: React.ReactNode;
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
  routinesButton?: (data: any) => React.ReactElement;
  selectedAssetId?: string;
};

export const AssetsList = ({
  onAssetClick,
  maxHeight,
  paddingBottom,
  onAssetsCountChange,
  updateRowsSelected,
  preserveScrollKey,
  addToGroupsComponent,
  selectionStatus,
  onSelectionStatusChange,
  onDataLoaded,
  customEmptyState,
  routinesButton,
  selectedAssetId,
}: AssetListProps) => {
  // TODO: Crop Circle Redesign: Remove after feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // TODO: Crop Circle Redesign: Remove old AssetsList after feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return (
      <AssetsListOld
        onAssetClick={onAssetClick}
        maxHeight={maxHeight}
        paddingBottom={paddingBottom}
        onAssetsCountChange={onAssetsCountChange}
        updateRowsSelected={updateRowsSelected}
        preserveScrollKey={preserveScrollKey}
        addToGroupsComponent={addToGroupsComponent}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        onDataLoaded={onDataLoaded}
        customEmptyState={customEmptyState}
        routinesButton={routinesButton}
      />
    );
  }

  // TODO: Crop Circle Redesign: Remove to AssetsListBase after feature is fully rolled out
  return (
    <AssetsListBase
      onAssetClick={onAssetClick}
      maxHeight={maxHeight}
      paddingBottom={paddingBottom}
      onAssetsCountChange={onAssetsCountChange}
      updateRowsSelected={updateRowsSelected}
      preserveScrollKey={preserveScrollKey}
      addToGroupsComponent={addToGroupsComponent}
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      onDataLoaded={onDataLoaded}
      customEmptyState={customEmptyState}
      routinesButton={routinesButton}
      selectedAssetId={selectedAssetId}
    />
  );
};

const AssetsListBase = ({
  onAssetClick,
  maxHeight,
  paddingBottom,
  onAssetsCountChange,
  updateRowsSelected,
  preserveScrollKey,
  addToGroupsComponent,
  selectionStatus,
  onSelectionStatusChange,
  onDataLoaded,
  customEmptyState,
  routinesButton,
  selectedAssetId,
}: AssetListProps) => {
  const { t } = useTranslation();

  const { data } = useAssetListFilter();
  const { data: orderData } = useAssetListOrder();
  const { mutateAsync: setOrder } = useSetAssetListOrder();
  const workspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();
  const showImpersonateWorkspace = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const setOrderData = useCallback(
    (order: any) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  const { columns, exportColumns } = useColumns(
    showImpersonateWorkspace,
    filter,
    routinesButton
  ) as {
    columns: ColumnDef<any, any>[];
    exportColumns: ColumnDef<any, any>[];
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;

    const isOEM = workspace?.type === WorkspaceType.Oem;
    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    if (f.groups?.length === 0) delete f.groups;
    
    // Filter out productName if not OEM (but don't auto-add it for OEM - respect user's filter choice)
    if (f.columns && !isOEM) {
      f.columns = f.columns.filter((col: string) => col !== 'productName');
    }
    setFilter(f);
  }, [data, workspace]);

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="asset"
      query={LIST_ASSETS}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columns}
      onRowClick={onAssetClick}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={customEmptyState || t('assets.textEmpty')}
      onItemsCountChange={onAssetsCountChange}
      dataPrefix="asset.listAssets"
      onOrderByChange={setOrderData}
      updateRowsSelected={workspace ? updateRowsSelected : undefined}
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      headerContent={addToGroupsComponent}
      exportColumns={exportColumns}
      exportFileName="assets"
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      onDataLoaded={onDataLoaded}
      highlightSelectedRow={true}
      selectedRowId={selectedAssetId}
    />
  );
};
